import React, { Fragment, useEffect, useCallback, useState, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { Prompt } from "react-router-dom";
import { useParams } from "react-router";
import AccountTree from "@mui/icons-material/AccountTree";
import { useTranslation } from "react-i18next";
import { required, shape } from "@totalsoft/pure-validations";
import { useDirtyFieldValidation, isValid } from "@totalsoft/pure-validations-react";
import { useQueryWithSimpleErrorHandling } from 'hooks/apolloWrappers';
import { FakeText, Card, IconButton, useToast } from "@totalsoft/rocket-ui";
import { useMutation } from "@apollo/client";
import { get, useChangeTrackingLens } from "@totalsoft/change-tracking-react";
import { isDirty } from "@totalsoft/change-tracking";
import { GET_PARTNER_HIERARCHY } from "../queries";
import { SAVE_PARTNER_HIERARCHY } from "../mutations";
import HierarchyGeneralDataComponent from "./HierarchyGeneralDataComponent";
import HierarchyDetailsTreeContainer from "./HierarchyDetailsTreeContainer";
import { hasAuthorization } from "utils/functions";
import * as rightsCodes from "constants/rightsCodes";
import { RightsContext } from "providers/RightsContext";
import HeaderPortal from "components/layout/HeaderPortal";
import { useHistory } from "react-router-dom";
import { useSimpleError } from "hooks/apolloWrappers";

const defaultPartnerHierarchy = {
  partnerHierarchyId: null,
  hierarchyCode: "",
  hierarchyName: "",
  isActive: false
};

const hierarchyHeaderValidation = shape({
  hierarchyCode: required,
  hierarchyName: required
});

const HierarchyContainer = () => {
  const rights = useContext(RightsContext);
  const canWrite = hasAuthorization(rightsCodes.MASTER_PARTNER_HIERARCHIES_WRITE, rights);
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(defaultPartnerHierarchy);
  const [title, setTitle] = useState("");
  const [validation, validate, resetValidation] = useDirtyFieldValidation(hierarchyHeaderValidation);
  const showError = useSimpleError();
  const showToast = useToast();
  const [t] = useTranslation();
  const params = useParams();
  const partnerHierarchyId = parseInt(params?.partnerHierarchyId);
  const history = useHistory();

  useEffect(() => {
    validate(get(lens), dirtyInfo);
  }, [validate, lens, dirtyInfo]);

  const { loading } = useQueryWithSimpleErrorHandling(GET_PARTNER_HIERARCHY, {
    variables: { partnerHierarchyId },
    skip: !partnerHierarchyId,
    onCompleted: data => {
      resetLens(data?.getPartnerHierarchy);
      setTitle(data?.getPartnerHierarchy?.hierarchyName);
      resetValidation();
    }
  });

  const handleRedirect = useCallback(
    partnerHierarchyId => {
      history.push(`/partnerHierarchies/${partnerHierarchyId}`);
    },
    [history]
  );

  const [addOrPartnerHierarchy] = useMutation(SAVE_PARTNER_HIERARCHY, {
    onError: error => {
      showError(error);
      resetLens(prev => ({ ...prev }));
    },
    onCompleted: data => {
      resetLens(data?.savePartnerHierarchy?.partnerHierarchy);
      resetValidation();
      if (!partnerHierarchyId) handleRedirect(data?.savePartnerHierarchy?.partnerHierarchy?.partnerHierarchyId);
    },
    update: cache => {
      showToast(t("PartnerHierarchy.PartnerHierarchySaved"), "success", { autoClose: 3000 });
      cache.evict({ id: "ROOT_QUERY", fieldName: "partnerHierarchies" });
      cache.gc();
    }
  });

  const hasChanges = useMemo(() => isDirty(dirtyInfo), [dirtyInfo]);

  const handleSave = useCallback(() => {
    const hierarchy = lens |> get;
    if (!validate(hierarchy)) return;
    const toSave = hierarchy;
    addOrPartnerHierarchy({ variables: { input: toSave } });
  }, [lens, addOrPartnerHierarchy, validate]);

  return loading ? (
    <Card sx={{ my: 1 }}>
      <FakeText lines={10} />
    </Card>
  ) : (
    <Fragment>
      <Prompt when={hasChanges && Boolean(partnerHierarchyId)} message={t("LeavingWithoutSaving")} />
      <HeaderPortal customTitle={title} inEdit={true} />
      <Card
        icon={AccountTree}
        iconColor="primary"
        title={t("PartnerHierarchy.GeneralData")}
        actions={
          <IconButton
            type="save"
            fontSize="medium"
            color="transparent"
            tooltip={t("Save")}
            onClick={handleSave}
            disabled={!canWrite || !hasChanges || !isValid(validation)}
          />
        }
      >
        <HierarchyGeneralDataComponent hierarchyHeader={lens} validation={validation} canWrite={canWrite} />
      </Card>
      {Boolean(partnerHierarchyId) && (
        <HierarchyDetailsTreeContainer
          partnerHierarchyId={partnerHierarchyId}
          hierarchyName={lens.hierarchyName |> get}
          canWrite={canWrite}
        />
      )}
    </Fragment>
  );
};

HierarchyContainer.propTypes = {
  canWrite: PropTypes.bool,
  partnerHierarchyId: PropTypes.number,
  onRedirect: PropTypes.func,
  onChangeTitle: PropTypes.func
};

export default HierarchyContainer;
